import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPassword() {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setError(null);
      setSuccess(false);

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        data.email,
        {
          redirectTo: `${window.location.origin}/reset-password?type=recovery`,
        }
      );

      if (resetError) throw resetError;

      setSuccess(true);
    } catch (error) {
      console.error('Failed to send reset email:', error);
      setError(error instanceof Error ? error.message : 'Failed to send reset email');
    }
  };

  return (
    <div className="min-h-screen bg-[--bg-secondary] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-[--text-primary]">
          Reset your password
        </h2>
        <p className="mt-2 text-center text-sm text-[--text-secondary]">
          Enter your email to receive a password‑reset link.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-[--bg-primary] py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {success ? (
            <div className="rounded-md bg-success-50 dark:bg-success-900 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-success-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-success-800 dark:text-success-200">
                    Reset link sent
                  </h3>
                  <div className="mt-2 text-sm text-success-700 dark:text-success-300">
                    <p>
                      Check your email for a link to reset your password. If it doesn't appear within a few minutes, check your spam folder.
                    </p>
                  </div>
                  <div className="mt-4">
                    <div className="-mx-2 -my-1.5 flex">
                      <Link
                        to="/login"
                        className="px-2 py-1.5 text-sm font-medium text-success-800 dark:text-success-200 hover:text-success-700 dark:hover:text-success-100"
                      >
                        Return to login
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-4 text-sm text-warning-700 bg-warning-100 rounded-lg dark:bg-[rgb(var(--warning-900)/0.2)] dark:text-warning-200" role="alert">
                  {error}
                </div>
              )}

              <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[--text-primary]">
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      {...register('email')}
                      type="email"
                      className="appearance-none block w-full px-3 py-2 border border-[--border-secondary] rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm bg-[--bg-primary] text-[--text-primary]"
                    />
                    {errors.email && (
                      <p className="mt-2 text-sm text-error-600">{errors.email.message}</p>
                    )}
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending reset link...' : 'Send reset link'}
                  </button>
                </div>
              </form>

              <div className="mt-6">
                <div className="relative">
                  <div className="relative flex justify-center text-sm">
                    <Link
                      to="/login"
                      className="font-medium text-[--link-default] hover:text-primary-500"
                    >
                      Back to login
                    </Link>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}